import { Notification, NotificationQuery } from "../types/notification";
import { BackendHttpService } from "./backendHttpService";
import { RequestOptions } from "./baseService";

/**
 * Notification Service
 *
 * All notifications are now person-specific. The API endpoints are:
 * - GET /notifications/person/{personId}
 * - PUT /notifications/person/{personId}/mark-all-read
 * - PUT /notifications/{id}/mark-read
 * - POST /notifications/generate
 * - POST /notifications/generate/person/{personId}
 * - POST /notifications/generate/system-notifications/person/{personId}
 * - GET /notifications/configurations
 * - PUT /notifications/configurations/{id}
 * - GET /notifications/person/{personId}/unread-count
 */
export class NotificationService extends BackendHttpService {
  constructor() {
    super("notifications");
  }

  /**
   * Get notifications for a specific person
   * GET /notifications/person/{personId}
   */
  async getPersonNotifications(
    personId: number,
    query?: NotificationQuery,
    options: RequestOptions = {}
  ): Promise<Notification[]> {
    try {
      if (!personId || isNaN(personId)) {
        throw new Error("Invalid person ID for notifications");
      }

      const params = new URLSearchParams();

      if (query?.isRead !== undefined) {
        params.append("isRead", query.isRead.toString());
      }

      if (query?.take !== undefined) {
        params.append("take", query.take.toString());
      } else {
        params.append("take", "50"); // Default value
      }

      if (query?.skip !== undefined) {
        params.append("skip", query.skip.toString());
      } else {
        params.append("skip", "0"); // Default value
      }

      const queryString = params.toString();
      const path = `/person/${personId}${queryString ? `?${queryString}` : ""}`;

      console.log(
        `[NotificationService] Fetching notifications for person ${personId}`,
        {
          path: this.buildUrl(path),
          query,
          queryString,
        }
      );

      const result = await this.get<Notification[]>(path, options);
      console.log(
        `[NotificationService] Successfully fetched ${result.length} notifications for person ${personId}`
      );
      return result;
    } catch (error) {
      console.error(
        `[NotificationService] Error fetching notifications for person ${personId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Mark a notification as read
   * PUT /notifications/{id}/mark-read
   */
  async markNotificationAsRead(
    notificationId: number,
    options: RequestOptions = {}
  ): Promise<void> {
    try {
      if (!notificationId || isNaN(notificationId)) {
        throw new Error("Invalid notification ID for mark as read");
      }

      // Use custom implementation to handle empty response
      await this.markAsReadRequest(notificationId, options);
    } catch (error) {
      console.error(
        `Error marking notification ${notificationId} as read:`,
        error
      );
      throw error;
    }
  }

  /**
   * Custom method to handle mark as read request with empty response
   */
  private async markAsReadRequest(
    notificationId: number,
    options: RequestOptions = {}
  ): Promise<void> {
    const accessToken = await this.getAccessToken();

    const headers = {
      ...this.getHeaders(),
      Authorization: `Bearer ${accessToken}`,
    };

    const httpResponse = await this.fetchWithTimeout(
      this.buildUrl(`${notificationId}/mark-read`),
      {
        method: "PUT",
        headers,
        body: JSON.stringify({}),
      },
      options
    );

    if (!httpResponse.ok) {
      throw new Error(`HTTP error! status: ${httpResponse.status}`);
    }

    // Don't try to parse JSON for empty responses
    // The endpoint returns Ok() which is an empty response
  }

  /**
   * Mark all notifications as read for a person
   * PUT /notifications/person/{personId}/mark-all-read
   */
  async markAllNotificationsAsRead(
    personId: number,
    options: RequestOptions = {}
  ): Promise<number> {
    try {
      if (!personId || isNaN(personId)) {
        throw new Error("Invalid person ID for mark all as read");
      }

      return await this.put<{}, number>(
        {},
        `/person/${personId}/mark-all-read`,
        options
      );
    } catch (error) {
      console.error(
        `Error marking all notifications as read for person ${personId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Get unread notifications count for a specific person
   * GET /notifications/person/{personId}/unread-count
   */
  async getPersonUnreadCount(
    personId: number,
    options: RequestOptions = {}
  ): Promise<number> {
    try {
      if (!personId || isNaN(personId)) {
        throw new Error("Invalid person ID for unread count");
      }

      return await this.get<number>(
        `/person/${personId}/unread-count`,
        options
      );
    } catch (error) {
      console.error(
        `Error fetching unread notifications count for person ${personId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Mark multiple notifications as read
   * Helper method to mark multiple notifications as read
   */
  async markMultipleAsRead(
    notificationIds: number[],
    options: RequestOptions = {}
  ): Promise<void> {
    try {
      const promises = notificationIds.map((id) =>
        this.markNotificationAsRead(id, options)
      );
      await Promise.all(promises);
    } catch (error) {
      console.error("Error marking multiple notifications as read:", error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const notificationService = new NotificationService();
